"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const xPercent = (clientX / innerWidth - 0.5) * 12;
      const yPercent = (clientY / innerHeight - 0.5) * 8;
      heroRef.current.style.setProperty("--mouse-x", `${xPercent}px`);
      heroRef.current.style.setProperty("--mouse-y", `${yPercent}px`);
    };
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: "linear-gradient(160deg, #FDF8F4 0%, #EBF4FA 40%, #FDF0ED 100%)" }}>
      
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(ellipse, rgba(45, 106, 143, 0.10) 0%, transparent 70%)", transform: "translate(20%, -20%)" }} />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(ellipse, rgba(232, 165, 152, 0.14) 0%, transparent 70%)", transform: "translate(-20%, 20%)" }} />
      <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(ellipse, rgba(90, 138, 111, 0.08) 0%, transparent 70%)", transform: "translate(-50%, -50%)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left: Text */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="animate-fade-up" style={{ animationDelay: "0.1s", animationFillMode: "both" }}>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest"
              style={{ background: "rgba(90, 138, 111, 0.12)", color: "#3D6B52", border: "1px solid rgba(90, 138, 111, 0.20)" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-growth animate-pulse-soft inline-block" />
                Private · Debt-Free · Community-Powered
              </span>
            </div>

            {/* Headline */}
            <div className="animate-fade-up" style={{ animationDelay: "0.2s", animationFillMode: "both" }}>
              <h1 className="font-heading font-bold leading-[1.08] tracking-tight text-ink"
              style={{ fontSize: "clamp(2.6rem, 5.5vw, 4.2rem)" }}>
                Fund Your Fertility{" "}
                <span className="relative inline-block">
                  <span className="text-gradient-primary">Journey</span>
                </span>{" "}
                with Love,{" "}
                <span className="text-gradient-warm">Not Loans.</span>
              </h1>
            </div>

            {/* Subheadline */}
            <div className="animate-fade-up" style={{ animationDelay: "0.3s", animationFillMode: "both" }}>
              <p className="text-lg md:text-xl text-ink-muted leading-relaxed max-w-xl">
                BabyBank lets you create <strong className="text-ink font-semibold">private campaigns</strong> so friends and family can support your path to parenthood — debt-free and stigma-free.
              </p>
            </div>

            {/* CTAs */}
            <div className="animate-fade-up flex flex-col sm:flex-row gap-3" style={{ animationDelay: "0.4s", animationFillMode: "both" }}>
              <Link href="/create-campaign" className="btn-primary text-base px-8 py-4">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Start Your Campaign
              </Link>
              <a href="#how-it-works" className="btn-secondary text-base px-8 py-4">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" />
                </svg>
                Learn How It Works
              </a>
            </div>

            {/* Trust signals */}
            <div className="animate-fade-up flex flex-wrap gap-4 pt-2" style={{ animationDelay: "0.5s", animationFillMode: "both" }}>
              {[
              { icon: "🔒", text: "Private & Secure" },
              { icon: "✨", text: "No Fees for Basic Use" },
              { icon: "🏥", text: "Clinic-Partnered" }].
              map(({ icon, text }) =>
              <div key={text} className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium text-ink-muted"
              style={{ background: "rgba(255,255,255,0.8)", border: "1px solid rgba(237,229,219,0.8)" }}>
                  <span aria-hidden="true">{icon}</span>
                  {text}
                </div>
              )}
            </div>
          </div>

          {/* Right: Visual */}
          <div className="relative lg:h-[580px] flex items-center justify-center">
            {/* Main image */}
            <div className="animate-scale-in relative w-full max-w-md mx-auto rounded-4xl overflow-hidden shadow-warm-lg"
            style={{ animationDelay: "0.3s", animationFillMode: "both", aspectRatio: "4/5" }}>
              <AppImage
                src="https://images.unsplash.com/photo-1513267000941-598abe7be16f"
                alt="Couple holding hands outdoors in warm afternoon light, hopeful and connected"
                fill
                className="object-cover" />
              
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 50%, rgba(45, 106, 143, 0.15) 100%)" }} />
            </div>

            {/* Floating card 1: Progress */}
            <div className="animate-float absolute -left-4 top-16 glass-card rounded-2xl p-4 max-w-[200px] shadow-glass">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #2D6A8F, #4A8DB5)" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402 0-3.791 3.068-5.191 5.281-5.191 1.312 0 4.151.501 5.719 4.457 1.59-3.968 4.464-4.447 5.726-4.447 2.54 0 5.274 1.621 5.274 5.181 0 4.069-5.136 8.625-11 14.402z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold text-ink leading-tight">Sarah & Mike</p>
                  <p className="text-2xs text-ink-muted">IVF Campaign</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-ink-muted">Raised</span>
                  <span className="text-sm font-bold text-primary">$14,200</span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: "rgba(45, 106, 143, 0.12)" }}>
                  <div className="h-full rounded-full progress-bar-fill"
                  style={{ "--progress-width": "71%", width: "71%", background: "linear-gradient(90deg, #2D6A8F, #5A8A6F)" } as React.CSSProperties} />
                </div>
                <p className="text-2xs text-ink-soft">71% of $20,000 goal</p>
              </div>
            </div>

            {/* Floating card 2: Donors */}
            <div className="animate-float-delayed absolute -right-4 bottom-24 glass-card rounded-2xl p-4 max-w-[190px] shadow-glass">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="flex -space-x-2">
                  {["#E8A598", "#2D6A8F", "#5A8A6F", "#D4806F"].map((color, i) =>
                  <div key={i} className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-white text-2xs font-bold"
                  style={{ background: color }}>
                      {["J", "M", "A", "K"][i]}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-sm font-bold text-ink">24 supporters</p>
              <p className="text-2xs text-ink-muted mt-0.5">contributed privately</p>
              <div className="mt-2 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-growth animate-pulse-soft inline-block" />
                <span className="text-2xs text-growth font-medium">3 new today</span>
              </div>
            </div>

            {/* Floating card 3: Privacy badge */}
            <div className="animate-float-slow absolute right-8 top-8 glass-card rounded-xl px-3 py-2 shadow-glass">
              <div className="flex items-center gap-2">
                <span className="text-sm" aria-hidden="true">🔒</span>
                <span className="text-xs font-semibold text-ink">100% Private</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-pulse-soft">
        <span className="text-xs text-ink-soft font-medium tracking-widest uppercase">Scroll</span>
        <div className="w-5 h-8 rounded-full border-2 border-ink-soft/40 flex items-start justify-center p-1">
          <div className="w-1 h-2 rounded-full bg-ink-soft/60 animate-bounce" />
        </div>
      </div>
    </section>);

}